// The critical input-file problems a user must fix before a file can be
// processed. The backend's blocking rule (inputfile_checker.blocking_findings):
// critical findings about the data — a checker crash (checker_error) is ours,
// not the user's, and never blocks.

const NON_BLOCKING = new Set(["checker_error"]);

export function blockingFindings(findings) {
  return (findings || []).filter(
    (f) => (f?.severity || "").toLowerCase() === "critical" && !NON_BLOCKING.has(f?.check)
  );
}

// From a failed upload or run start: { inputFile, message, findings } when the
// response carries input-file problems, else null. A refused run returns
// detail = { input_file, findings }; an upload with errors returns
// detail = the validation record ({ filename, validation: { findings } }).
export function problemsFromError(error) {
  const body = error?.response?.body;
  const detail = body?.detail;
  if (!detail || typeof detail !== "object") return null;
  const findings = blockingFindings(detail.findings || detail.validation?.findings);
  if (findings.length === 0) return null;
  return {
    inputFile: detail.input_file || detail.filename || null,
    message: body.message || null,
    findings,
  };
}

// Plain titles for the checks that can block a file (critical checks in
// inputfile_checker.SEVERITY_BY_CHECK); anything else gets its id prettified.
const CHECK_TITLES = {
  range_invalid_data_types: "Text in a cell that needs a number",
  replicates_unbalanced: "Missing replicate",
  inconsistent_time_grid: "Missing or extra timepoints",
  rows_without_keys: "Content outside the data table",
  header_mismatch: "Column header doesn't match the template",
  condition_no_not_1n: "Condition numbering",
  replicate_no_not_1n: "Replicate numbering",
  condition_id_multi_no: "One condition ID with several condition numbers",
  condition_no_multi_id: "One condition number with several condition IDs",
  t0_blank_measurement: "Missing day-0 measurement",
  invalid_clones: "Invalid condition ID",
};

export function checkLabel(check) {
  if (CHECK_TITLES[check]) return CHECK_TITLES[check];
  const words = String(check || "problem").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// [{title, items}] grouped by check, in first-seen order.
export function groupByCheck(findings) {
  const groups = new Map();
  for (const f of findings || []) {
    const key = f.check || "problem";
    if (!groups.has(key)) groups.set(key, { check: key, title: checkLabel(key), items: [] });
    groups.get(key).items.push(f);
  }
  return [...groups.values()];
}
